/**
 * Heat Pump Confidence Weighting Module
 * 
 * Applies confidence-based adjustments to heat pump SPF based on:
 * - Emitter suitability (unknown/poor emitters reduce confidence)
 * - Insulation level (unknown insulation reduces confidence slightly)
 * - Pipe bore (unknown pipe bore reduces confidence)
 * 
 * This ensures heat pump scores and costs reflect real-world uncertainty
 * when critical suitability factors are unknown.
 */

import type { SystemRecInput } from './types';
import { deriveFlowTempBand } from './flowTempEstimation';

export interface ConfidenceFactors {
  /** Overall confidence multiplier (0.5 - 1.0) */
  overall: number;
  /** Individual factors that contributed to the confidence */
  breakdown: {
    emitterPenalty: number;
    insulationPenalty: number;
    pipeBorePenalty: number;
  };
  /** Reasons for reduced confidence (for user feedback) */
  reasons: string[];
}

/**
 * Calculate confidence factor for heat pump performance
 * 
 * Returns a multiplier (0.5 - 1.0) that adjusts optimistic SPF assumptions
 * based on known/unknown suitability factors.
 * 
 * @param input - User input with emitter, insulation, and other data
 * @returns Confidence factors with breakdown and reasons
 */
export function getConfidenceFactor(input: SystemRecInput): ConfidenceFactors {
  let factor = 1.0;
  const reasons: string[] = [];
  const breakdown = {
    emitterPenalty: 0,
    insulationPenalty: 0,
    pipeBorePenalty: 0,
  };

  // Emitter suitability is critical for heat pump performance
  const emitterSuitability = input.emitters?.lowTempSuitability;
  
  if (emitterSuitability === 'unknown') {
    // Unknown emitters: significant penalty (30%)
    // We don't know if they can handle low flow temps
    factor *= 0.70;
    breakdown.emitterPenalty = 0.30;
    reasons.push('Emitter suitability unknown - may require upgrades');
  } else if (emitterSuitability === 'definitely_needs_upgrade') {
    // Known poor emitters: larger penalty (45%)
    // Will definitely need upgrades or higher flow temps
    factor *= 0.55;
    breakdown.emitterPenalty = 0.45;
    reasons.push('Emitters need upgrades for optimal heat pump performance');
  } else if (emitterSuitability === 'likely_needs_upgrade') {
    // Likely needs upgrades: moderate penalty (35%)
    factor *= 0.65;
    breakdown.emitterPenalty = 0.35;
    reasons.push('Emitters likely need upgrades for best performance');
  }
  
  // Insulation affects heat demand and flow temperatures
  const insulationLevel = input.insulationLevel;
  
  if (insulationLevel === 'unknown') {
    // Unknown insulation: small penalty (10%)
    // Less critical than emitters, but affects demand
    factor *= 0.90;
    breakdown.insulationPenalty = 0.10;
    reasons.push('Insulation level unknown - may affect efficiency');
  }
  
  // Pipe bore affects ability to deliver heat at lower flow temps
  // Note: pipeBore is not in base SystemRecInput type - checking as optional extension
  const pipeBore = (input as any).pipeBore;
  
  if (pipeBore === 'unknown') {
    // Unknown pipe bore: small penalty (5%)
    factor *= 0.95;
    breakdown.pipeBorePenalty = 0.05;
    reasons.push('Pipe bore unknown - may limit flow');
  }
  
  // Ensure minimum confidence factor of 0.50
  // (Even with all unknowns, we assume at least 50% of optimistic SPF)
  factor = Math.max(0.50, factor);
  
  return {
    overall: factor,
    breakdown,
    reasons,
  };
}

/**
 * Calculate effective SPF for heat pumps with confidence weighting
 * 
 * Takes optimistic/assumed SPF and applies confidence factors based on
 * known/unknown suitability inputs.
 * 
 * @param assumedSPF - Optimistic SPF (e.g., 3.5 for ASHP, 4.0 for GSHP)
 * @param input - User input for confidence assessment
 * @returns Effective SPF clamped to reasonable bounds (1.8 - 4.5)
 */
export function getEffectiveSPF(assumedSPF: number, input: SystemRecInput): {
  effectiveSPF: number;
  confidence: ConfidenceFactors;
} {
  const confidence = getConfidenceFactor(input);
  let effectiveSPF = assumedSPF * confidence.overall;
  
  // Clamp to sane bounds:
  // - Minimum 1.8 (worse than resistance heating would be silly)
  // - Maximum 4.5 (realistic upper bound even in ideal conditions)
  effectiveSPF = Math.max(1.8, Math.min(4.5, effectiveSPF));
  
  return {
    effectiveSPF,
    confidence,
  };
}

/**
 * Calculate score penalty for heat pumps with unknown critical inputs
 * 
 * This is applied to the scoring system to reflect that heat pumps are
 * riskier when we don't know key suitability factors, or when they require
 * high flow temperatures that reduce efficiency significantly.
 * 
 * @param input - User input
 * @param heatLossKw - Optional heat loss for flow temp estimation
 * @returns Score penalty (0 to 10 points)
 */
export function getScorePenalty(input: SystemRecInput, heatLossKw?: number): {
  penalty: number;
  reasons: string[];
} {
  let penalty = 0;
  const reasons: string[] = [];
  
  // Critical unknown: emitter suitability
  const emitterSuitability = input.emitters?.lowTempSuitability;
  if (emitterSuitability === 'unknown') {
    penalty += 3;
    reasons.push('Emitter suitability unknown reduces confidence');
  } else if (emitterSuitability === 'definitely_needs_upgrade') {
    penalty += 2;
    reasons.push('Emitters need upgrades for heat pump');
  }
  
  // Minor unknown: pipe bore
  const pipeBore = (input as any).pipeBore;
  if (pipeBore === 'unknown') {
    penalty += 1;
    reasons.push('Pipe bore unknown may limit performance');
  }
  
  // NEW: Apply penalty based on flow temp band (from SPF range estimation)
  const { band } = deriveFlowTempBand(input, heatLossKw);
  
  if (band === '65_70') {
    // Very high flow temps = heat pump is not suitable
    penalty += 5;
    reasons.push('High flow temps required (65-70°C) - heat pump unsuitable without major upgrades');
  } else if (band === '55_65') {
    // High flow temps = heat pump efficiency will be poor
    penalty += 3;
    reasons.push('High flow temps required (55-65°C) - efficiency significantly reduced');
  } else if (band === '45_55') {
    // Medium flow temps = some efficiency penalty but workable
    penalty += 1;
    reasons.push('Medium flow temps (45-55°C) - moderate efficiency reduction');
  }
  // No penalty for 35_45 band (ideal for heat pumps)
  
  return {
    penalty,
    reasons,
  };
}
